import { useState, type ReactNode } from 'react';
import { Dialog } from '@paige/ui';
import { ProblemActionDialog, type PendingAction } from '../problems/ProblemActionDialog';
import type { ProblemActRequest, ProblemActResult } from '../problems/problem-types';
import { FlowDetail, type FlowLinks } from './FlowDetail';
import { FlowIncidentCard } from './FlowIncidentCard';
import type { FlowStatus } from './flow-state';
import type {
  AdminFlow,
  FlowIncident,
  FlowItemsPage,
  FlowItemsQuery,
  FlowPlace,
} from './flow-types';
import { placeName, placeRoad } from './flow-words';
import type { IncidentLoaders } from './IncidentWho';
import { FlowViewer } from './FlowViewer';
import type { ViewerAsk, ViewerTools } from './viewer-types';

/** The support viewer inside the drawer: the file asked for (null when none), how to ask, and how it reads. */
export interface DrawerViewer {
  ask: ViewerAsk | null;
  see: (ask: ViewerAsk) => void;
  close: () => void;
  tools: ViewerTools;
}

export interface FlowIncidentDrawerProps {
  flow: AdminFlow;
  /** The stage the drawer is about. */
  place: FlowPlace;
  status: FlowStatus;
  /** Every incident; the drawer shows those at its stage. */
  incidents: FlowIncident[];
  /** The incident to open first (from the verdict or the address); otherwise the worst here. */
  incidentId: string | null;
  onIncident: (id: string | null) => void;
  onClose: () => void;
  links: FlowLinks;
  loaders: IncidentLoaders;
  loadItems?: ((q: FlowItemsQuery) => Promise<FlowItemsPage>) | undefined;
  /** Does a staff action; absent for a role the API refuses (viewer and billing). */
  onAct?: ((request: ProblemActRequest) => Promise<ProblemActResult>) | undefined;
  onViewAsCustomer?: ((company: { id: string; name: string }, reason: string) => void) | undefined;
  /** Seeing a file inside the drawer; absent for a role that may not open a company's files. */
  viewer?: DrawerViewer | undefined;
}

/** The incidents at a stage, worst first; a slow stage's own incident only there. */
export function incidentsAt(incidents: FlowIncident[], place: FlowPlace): FlowIncident[] {
  return incidents.filter((i) =>
    i.kind === 'slow' ? i.origin === place : i.places.includes(place),
  );
}

function Quiet({ place }: { place: FlowPlace }): ReactNode {
  return (
    <p className="fl-inc__quiet">
      Nothing at {placeName[place]} is stuck, retrying or held. Everything here is moving; the
      counts below say what waits and what is being worked.
    </p>
  );
}

/** The worst incident opens by itself until someone closes it; the address then names none. */
function useOpened(
  here: FlowIncident[],
  incidentId: string | null,
  onIncident: (id: string | null) => void,
) {
  const [shut, setShut] = useState(false);
  const opened = incidentId ?? (shut ? null : (here[0]?.id ?? null));
  const toggle = (id: string) => {
    setShut(opened === id);
    onIncident(opened === id ? null : id);
  };
  return { opened, toggle };
}

function describe(place: FlowPlace, n: number): string {
  const what =
    n === 0
      ? 'Nothing needs anyone here.'
      : `${n} ${n === 1 ? 'thing is' : 'things are'} not simply flowing.`;
  return `${placeRoad[place]}. ${what}`;
}

/** Everything at the stage, item by item, under the incidents. */
function Everything(props: FlowIncidentDrawerProps): ReactNode {
  const { flow, place, status } = props;
  return (
    <>
      <h3 className="fl-drawer__h">Everything at {placeName[place]}</h3>
      <FlowDetail
        cell={flow.cells.find((c) => c.place === place)!}
        status={status}
        runs={place === 'engine' ? flow.runs : []}
        links={props.links}
        onClose={props.onClose}
        company={flow.company?.slug ?? null}
        stamp={String(status.here)}
        loadItems={props.loadItems}
      />
    </>
  );
}

/** The stage's incidents, worst first, each a card. */
function Incidents(
  props: FlowIncidentDrawerProps & { onAsk: ((p: PendingAction) => void) | undefined },
): ReactNode {
  const here = incidentsAt(props.incidents, props.place);
  const { opened, toggle } = useOpened(here, props.incidentId, props.onIncident);
  return (
    <ul className="fl-incs" aria-label="What is not simply flowing">
      {here.map((i) => (
        <FlowIncidentCard
          key={i.id}
          loaders={props.loaders}
          links={props.links}
          logsProject={props.flow.logsProject}
          onAsk={props.onAsk}
          onViewAsCustomer={props.onViewAsCustomer}
          onSee={props.viewer?.see}
          incident={i}
          open={opened === i.id}
          onToggle={() => toggle(i.id)}
          stamp={`${i.items}|${i.acked}|${i.settling}`}
        />
      ))}
    </ul>
  );
}

/**
 * A stage clicked open: a drawer over the page (Esc closes it; the address names the stage and the incident, so it
 * can be sent to someone), its incidents worst first, each folded across companies with what to do, then
 * everything at the stage, item by item. What the drawer lists is read when it opens and again only when its
 * counts change, never on the picture's every poll.
 */
export function FlowIncidentDrawer(props: FlowIncidentDrawerProps): ReactNode {
  const { place, incidents, onClose, onAct } = props;
  const [pending, setPending] = useState<PendingAction | null>(null);
  const count = incidentsAt(incidents, place).length;
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      variant="drawer"
      size="lg"
      title={placeName[place]}
      description={describe(place, count)}
    >
      <div className="fl-drawer" data-drawer={place}>
        {props.viewer?.ask ? (
          <FlowViewer
            ask={props.viewer.ask}
            tools={props.viewer.tools}
            onBack={props.viewer.close}
          />
        ) : (
          <>
            {count === 0 ? <Quiet place={place} /> : null}
            <Incidents {...props} onAsk={onAct ? setPending : undefined} />
            <Everything {...props} />
          </>
        )}
      </div>
      {onAct ? (
        <ProblemActionDialog pending={pending} onClose={() => setPending(null)} onConfirm={onAct} />
      ) : null}
    </Dialog>
  );
}
